import React, { useState, useEffect } from 'react';
import cloudSyncService, { SyncConfig } from '../services/cloudSync';

const CloudSyncPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<'success' | 'error' | 'info'>('info');
  const [syncConfig, setSyncConfig] = useState<SyncConfig>(cloudSyncService.getSyncConfig());
  
  // 加载同步配置
  useEffect(() => {
    setSyncConfig(cloudSyncService.getSyncConfig());
  }, []);
  
  // 保存并验证配置
  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setStatusMessage('正在保存配置...');
    setStatusType('info');
    
    // 保存配置到服务
    cloudSyncService.saveSyncConfig(syncConfig);
    
    // 如果启用了同步，测试连接
    if (syncConfig.enabled) {
      setStatusMessage('正在测试连接...');
      const testResult = await cloudSyncService.testConnection();
      
      if (testResult.success) {
        setStatusMessage(`连接成功: ${testResult.message}`);
        setStatusType('success');
      } else {
        setStatusMessage(`连接失败: ${testResult.message}`);
        setStatusType('error');
      }
    } else {
      setStatusMessage('配置已保存，云同步未启用');
      setStatusType('info');
    }
    
    setIsLoading(false);
  };
  
  // 执行同步
  const handleSync = async () => {
    if (!cloudSyncService.isSyncEnabled()) {
      setStatusMessage('请先配置并启用云同步');
      setStatusType('error');
      return;
    }
    
    setIsLoading(true);
    setStatusMessage('正在同步...');
    setStatusType('info');
    
    try {
      const result = await cloudSyncService.syncRecords();
      
      if (result.success) {
        setStatusMessage(`同步成功: 上传了 ${result.uploadedCount} 条记录，下载了 ${result.downloadedCount} 条记录`);
        setStatusType('success');
      } else {
        setStatusMessage(`同步失败: ${result.message}`);
        setStatusType('error');
      }
    } catch (error) {
      setStatusMessage(`同步异常: ${error instanceof Error ? error.message : String(error)}`);
      setStatusType('error');
    }
    
    setIsLoading(false);
  };
  
  // 生成用户ID
  const generateUserId = () => {
    const newUserId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    setSyncConfig(prev => ({ ...prev, userId: newUserId }));
  };
  
  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
        >
          <svg
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          云同步配置
        </button>
        
        {cloudSyncService.isSyncEnabled() && (
          <button
            onClick={handleSync}
            disabled={isLoading}
            className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md flex items-center disabled:opacity-50"
          >
            {isLoading ? '同步中...' : '立即同步'}
          </button>
        )}
      </div>

      {isOpen && (
        <div className="mt-4 p-4 bg-white shadow-lg rounded-lg border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cloudflare Worker 云同步设置</h3>
          
          {statusMessage && (
            <div className={`mb-4 p-3 rounded-md ${
              statusType === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : statusType === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}>
              {statusMessage}
            </div>
          )}
          
          <form onSubmit={handleSaveConfig}>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableSync"
                  checked={syncConfig.enabled}
                  onChange={(e) => setSyncConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="enableSync" className="ml-2 block text-sm text-gray-900">
                  启用云同步
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Worker URL</label>
                <input
                  type="text"
                  value={syncConfig.workerUrl}
                  onChange={(e) => setSyncConfig(prev => ({ ...prev, workerUrl: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                  placeholder="https://your-worker.your-subdomain.workers.dev"
                />
                <p className="mt-1 text-xs text-gray-500">输入您的Cloudflare Worker的URL地址</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">API密钥</label>
                <input
                  type="password"
                  value={syncConfig.apiKey}
                  onChange={(e) => setSyncConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                  placeholder="您的API密钥"
                />
                <p className="mt-1 text-xs text-gray-500">输入用于验证身份的API密钥</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">用户ID</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    value={syncConfig.userId}
                    onChange={(e) => setSyncConfig(prev => ({ ...prev, userId: e.target.value }))}
                    className="block w-full rounded-l-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                    placeholder="您的唯一用户ID"
                  />
                  <button
                    type="button"
                    onClick={generateUserId}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm"
                  >
                    生成
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">用于区分不同用户的数据，请保持唯一性</p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoSync"
                  checked={syncConfig.autoSync}
                  onChange={(e) => setSyncConfig(prev => ({ ...prev, autoSync: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="autoSync" className="ml-2 block text-sm text-gray-900">
                  启用自动同步
                </label>
              </div>

              {syncConfig.autoSync && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">同步间隔（分钟）</label>
                  <input
                    type="number"
                    min="5"
                    max="1440"
                    value={syncConfig.syncInterval}
                    onChange={(e) => setSyncConfig(prev => ({ ...prev, syncInterval: parseInt(e.target.value) || 60 }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {isLoading ? '处理中...' : '保存设置'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CloudSyncPanel; 